import React, { useState, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Paper, Typography } from '@mui/material';
import { AppButton } from '../../AppButton';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useStyles } from '../styles';
import { EventFormJsonFields } from '../../../utils/interfaces';
import { useAppFormContext } from '../../../utils/formContext';
import { handleFiles } from '../../../utils/helpers';
import KenColors from '../../../utils/kenColors';
import KenTextLabel from '../../KenTextLabel';
import KenIcon from '../../KenIcon';
import Image from '../../../utils/assetHelper';
import KenLoader from '../../KenLoader';
import { enqueueSnackbar } from 'notistack';
import ClearIcon from '@mui/icons-material/Clear';

const FileUpload: React.FunctionComponent<{ field: EventFormJsonFields }> = (props) => {
  const { field }:any = props;
  console.log(field, 'field11111');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { register, control, getValues, setValue } = useFormContext();

  const classes = useStyles();
  const {
    state: { files },
    dispatch,
  } = useAppFormContext();
  const [loading, setLoading] = useState(false);

const fileHandler = async (e: React.ChangeEvent<HTMLInputElement>, fields: any) => {
  setLoading(true);
  try {
    const file = e.target.files?.[0];
    if (!file) {
      setLoading(false);
      return;
    }

    // File validation
    if (fields?.originalName === 'speakerImage' || fields?.type === 'coverField') {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        enqueueSnackbar({ message: 'Only JPG and PNG files are allowed', variant: 'error', autoHideDuration: 1000 });
        e.target.value = '';
        setLoading(false);
        return;
      }

      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 2) {
        enqueueSnackbar({ message: 'File size should not exceed 2 MB', variant: 'error', autoHideDuration: 1000 });
        e.target.value = '';
        setLoading(false);
        return;
      }
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result as string;
      
      // Remove the "data:image/jpeg;base64," prefix to get pure base64 for API
      const pureBase64 = base64String.split(',')[1];
      
      // Store file data in context with both versions
      dispatch({ 
        type: 'updateFiles', 
        value: { 
          fieldName: fields?.name, 
          fileData: { 
            data: base64String, // Full data URL for UI display
            pureData: pureBase64, // Pure base64 for API payload
            name: file.name,
            size: file.size,
            type: file.type
          } 
        } 
      });

      // Update form value with proper structure - only imageUrl (pure base64) and fileName
      if (fields?.type === 'coverField') {
        setValue(fields.name, {
          imageUrl: pureBase64, // Pure base64 for API payload (no prefix)
          fileName: file.name
        });
      } else if (fields?.type === 'upload') {
        setValue(fields.name, {
          imageUrl: pureBase64, // Pure base64 for API payload (no prefix)
          fileName: file.name
        });
      }

      setLoading(false);
    };

    reader.readAsDataURL(file);
  } catch (error) {
    console.error('Error processing file:', error);
    enqueueSnackbar({ message: 'Error processing file', variant: 'error', autoHideDuration: 1000 });
    setLoading(false);
  }
};

  const FileComponent: React.FunctionComponent = () => {
    switch (field.type) {
      case 'coverField':
        const coverImageData:any = files[`${field?.name}`] || getValues(`${field.name}`);
        
        // Check if the imageUrl is a Salesforce URL (starts with http/https) or base64
        const isSalesforceUrl = coverImageData?.imageUrl && 
          (coverImageData.imageUrl.startsWith('http://') || coverImageData.imageUrl.startsWith('https://'));
        
        // For UI display:
        // - If it's a Salesforce URL from API, use it directly
        // - If it's base64 from new upload, construct the data URL
        // - If it's from files context, use the full data URL
        const coverImageUrl:any = files[`${field?.name}`]?.data || 
          (isSalesforceUrl ? coverImageData.imageUrl : 
           coverImageData?.imageUrl ? `data:image/jpeg;base64,${coverImageData.imageUrl}` : null);
        
        return (
          <Paper
            style={{
              background: coverImageUrl
                ? `url(${coverImageUrl})`
                : KenColors.ClientBlue,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              ...field.fieldStyle,
            }}
            className={classes.cardCoverPhoto}
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            <Controller
              control={control}
              render={({ field: { onChange } }) => {
                return (
                  <input
                    ref={fileInputRef}
                    accept={field?.accept || 'image/jpeg,image/png,image/jpg'}
                    id={field.name}
                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                      await fileHandler(e, field);
                      onChange(e);
                    }}
                    type="file"
                    style={{ display: 'none' }}
                  />
                );
              }}
              {...register(`${field.name}`)}
            />

            <AppButton
              width="220px"
              background={KenColors.kenWhite}
              className={classes.arrayButton}
              others={{
                padding: '12px !important',
              }}
              variant="contained"
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
            >
              {coverImageData?.fileName || coverImageData?.name || field.label}
            </AppButton>

            <Typography padding={'0px 0px 10px 0px'} className={classes.cardCoverFooter}>
              <div style={{ borderBottom: `${KenColors.ClientBlue4} solid 1px`, margin: '0px 20px 10px 20px' }} />
              {field?.description}
            </Typography>
          </Paper>
        );

      case 'upload':
        const uploadData:any = files[field.name as string] || getValues(`${field.name}`);
        
        // Check if the imageUrl is a Salesforce URL (starts with http/https) or base64
        const isUploadSalesforceUrl = uploadData?.imageUrl && 
          (uploadData.imageUrl.startsWith('http://') || uploadData.imageUrl.startsWith('https://'));
        
        // For UI display:
        // - If it's a Salesforce URL from API, use it directly
        // - If it's base64 from new upload, construct the data URL
        // - If it's from files context, use the full data URL
        const hasFiles = files[field.name as string]?.data || 
          (isUploadSalesforceUrl ? uploadData.imageUrl : 
           uploadData?.imageUrl ? `data:image/jpeg;base64,${uploadData.imageUrl}` : null);
        
        return (
          <Controller
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => {
              return (
                <>
                  {field.hideLabel && <KenTextLabel label={field.label} labelClassName={classes.inputLableCustom} />}
                  <div className={classes.customFieldContainer} style={{ margin: '6px 0px 8px 0px', ...field.fieldStyle }}>
                    <input
                      ref={fileInputRef}
                      id={field?.id}
                      type="file"
                      accept={field?.originalName === 'speakerImage' ? 'image/jpeg,image/png' : field.accept}
                      onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                        await fileHandler(e, field);
                        onChange(e);
                      }}
                      style={{ display: 'none', height: 300 }}
                    />
                    <Box display={'flex'} alignItems={'center'} gap={'8px'} width={'100%'}>
                      <Box width={'100%'} className={classes.customUploadField} border={`1px solid ${error?.message ? KenColors.statusRed : KenColors.grey}`}>
                        <h6
                          className={classes.customUploadFieldText}
                          style={{ 
                            color: hasFiles ? 'blue' : KenColors.ClientGrey, 
                            textDecoration: hasFiles ? 'underline' : 'none',
                            cursor: hasFiles ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (hasFiles) {
                              // Use the constructed full data URL for opening
                              const fileUrl = hasFiles;
                              if (fileUrl) {
                                window.open(fileUrl, '_blank');
                              }
                            } else {
                              fileInputRef.current?.click();
                            }
                          }}
                        >
                          {uploadData ? (uploadData?.fileName || uploadData?.name) : field.placeholder}
                        </h6>
                        <FileUploadOutlinedIcon
                          onClick={() => {
                            fileInputRef.current?.click();
                          }}
                          style={{ height: '26px', color: KenColors.grey, cursor: 'pointer' }}
                        />
                      </Box>
                      {hasFiles && (
                        <ClearIcon
                          style={{ cursor: 'pointer', color: KenColors.grey }}
                          onClick={() => {
                            dispatch({
                              type: 'removeFiles',
                              value: {
                                fieldName: field.name,
                              },
                            });
                            setValue(field.name, null);
                          }}
                        />
                      )}
                      {error?.message && <KenIcon iconType="img" icon={Image.Icons.Error} variant="extraSmall" />}
                    </Box>
                  </div>
                  {error?.message && (
                    <Typography textAlign={'start'} color={KenColors.red2} fontSize={'14px'} fontWeight={'500'}>
                      {error?.message}
                    </Typography>
                  )}
                </>
              );
            }}
            {...register(`${field.name}`, { ...field.validation })}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading && <KenLoader />}
      <FileComponent />
    </>
  );
};

export default FileUpload;